import { RefObject, useCallback } from 'react';
import { Control, FieldValues, Path, PathValue, useController } from 'react-hook-form';

import { Descendant, Editor } from 'slate';

import { FileAttachment } from '../../db/types';
import { ToolbarCustomizationProps } from '../../rich-text/Toolbar';
import RichTextEditorComp, { RichTextEditorProps, RichTextUploadAttachmentConfig } from '../RichText/Editor';

interface VMProps<T extends FieldValues> {
  editorRef?: RefObject<Editor>;
  control: Control<T, object>;
  inputField: {
    name: Path<T>;
    initialValue: PathValue<T, Path<T>> | undefined;
  };
  attachments?: {
    name: Path<T>;
    initialValue: PathValue<T, Path<T>>;
    uploadRequestConfig: RichTextUploadAttachmentConfig | undefined;
  };
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onContentStateChange?: ({ hasImage, hasFormula }: { hasImage: boolean; hasFormula: boolean }) => any;
  onAttachmentsChange?: RichTextEditorProps['onAttachmentsChange'];
}

const useRichTextEditorVM = <T extends FieldValues>({
  control,
  inputField,
  attachments,
  editorRef,
  onContentStateChange,
  onAttachmentsChange: _onAttachmentsChange,
  onChange: _onChange,
  onBlur: _onBlur,
}: VMProps<T>) => {
  const {
    field: { onChange: onInputChange, onBlur: onInputBlur },
    fieldState: { error: inputError },
  } = useController({
    name: inputField.name,
    control,
    defaultValue: inputField.initialValue || ('' as PathValue<T, Path<T>>),
  });

  const {
    field: { onChange: onInputAttachmentsChange },
    fieldState: { error: attachmentError },
  } = useController({
    name: attachments?.name || ('' as Path<T>),
    control,
    defaultValue: attachments?.initialValue || ([] as PathValue<T, Path<T>>),
  });

  const onChange = useCallback(
    (value: string) => {
      onInputChange(value);
      if (editorRef?.current && onContentStateChange) {
        const hasImage = editorRef.current.hasImage();
        const hasFormula = editorRef.current.hasFormula();
        onContentStateChange({ hasImage, hasFormula });
      }
      _onChange?.(value);
    },
    [onInputChange, _onChange, editorRef?.current, onContentStateChange]
  );

  const onBlur = useCallback(() => {
    onInputBlur();
    _onBlur?.();
  }, [onInputBlur, _onBlur]);

  const onAttachmentsChange = useCallback(
    (value: FileAttachment[]) => {
      onInputAttachmentsChange(value);
      _onAttachmentsChange?.(value ?? []);
    },
    [onInputAttachmentsChange, _onAttachmentsChange]
  );

  return { onChange, onBlur, onAttachmentsChange, error: inputError || attachmentError };
};

export type Props<T extends FieldValues> = VMProps<T> &
  Omit<RichTextEditorProps, 'onBlur' | 'onChange' | 'initialAttachments' | 'initialValue'> & {
    toolbar?: false | ToolbarCustomizationProps;
  };

const RichTextEditor = <T extends FieldValues>({ toolbar, ...props }: Props<T>) => {
  const { editorRef, inputField, attachments } = props;
  const { onChange, onBlur, onAttachmentsChange, error } = useRichTextEditorVM(props);

  return (
    <RichTextEditorComp
      {...props}
      ref={editorRef}
      initialValue={inputField.initialValue || ''}
      initialAttachments={attachments?.initialValue || []}
      onChange={onChange}
      onAttachmentsChange={onAttachmentsChange}
      onBlur={onBlur}
      toolbar={
        typeof toolbar === 'boolean'
          ? toolbar
          : {
              ...toolbar,
              uploadAttachmentConfig: attachments?.uploadRequestConfig,
            }
      }
      errorMessage={error?.message}
    />
  );
};

export default RichTextEditor;
